import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchSubCampaigns,
  fetchSubCampaignById,
  createSubCampaignApi,
  updateSubCampaignApi,
  deleteSubCampaignApi,
  duplicateSubCampaignApi,
  toggleSubCampaignApi,
  runSubCampaignApi,
  updateSubCampaignScheduleApi,
  assignSubCampaignTemplatesApi,
  assignSubCampaignRecipientsApi,
  fetchSubCampaignRecipients,
  bulkSubCampaignApi,
  previewSubCampaignSchedule,
  fetchSubCampaignAssignments,
  assignRecipientsToSubCampaignsApi,
  assignTemplatesToSubCampaignsApi,
} from '../Email-Marketing/shared/api.js';

// Replaces the Email-Marketing `useSubCampaignsStore` zustand store.

const EMPTY_SUMMARY = {
  total: 0, active: 0, paused: 0, draft: 0, completed: 0,
  recipients: 0, sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0,
};

export const DEFAULT_QUERY = {
  page: 1,
  limit: 25,
  status: 'all',
  search: '',
  sortBy: 'createdAt',
  sortDir: 'desc',
};

export const EMPTY_ASSIGNMENTS = { subCampaigns: [], recipients: {}, templates: {} };

/** Backend `_id` vs frontend `id`; give every row both so either key works. */
const normalise = (raw = {}) => {
  const id = String(raw._id || raw.id || '');
  const scheduling = raw.scheduling || {};
  return {
    ...raw,
    id,
    _id: id,
    campaignId: String(raw.campaign || raw.campaignId || ''),
    steps: Array.isArray(raw.steps) ? raw.steps : [],
    recipients: Array.isArray(raw.recipients) ? raw.recipients : undefined,
    statistics: raw.statistics || {},
    scheduling: {
      type: scheduling.type || 'scheduled',
      timezone: scheduling.timezone || 'UTC',
      scheduledAt: scheduling.scheduledAt || null,
      nextRunAt: scheduling.nextRunAt || null,
      lastRunAt: scheduling.lastRunAt || null,
      occurrencesRun: scheduling.occurrencesRun || 0,
      skipWeekends: Boolean(scheduling.skipWeekends),
      jitterMinutes: scheduling.jitterMinutes || 0,
      recurring: scheduling.recurring || null,
    },
  };
};

const sameId = (row, id) => String(row.id) === String(id) || String(row._id) === String(id);

export const loadSubCampaigns = createAsyncThunk(
  'emailSubCampaigns/load',
  async ({ campaignId, overrides = {} }, { getState, rejectWithValue }) => {
    if (!campaignId) return null;
    const key = String(campaignId);
    const query = { ...getState().emailSubCampaigns.query, ...overrides };
    try {
      const data = await fetchSubCampaigns(key, query);
      return {
        key,
        items: (data.subCampaigns || []).map(normalise),
        summary: data.summary || EMPTY_SUMMARY,
        pagination: data.pagination || null,
      };
    } catch (error) {
      console.error('Failed to load sub-campaigns:', error);
      return rejectWithValue(error.message || 'Could not load sub-campaigns');
    }
  }
);

export const createSubCampaign = createAsyncThunk(
  'emailSubCampaigns/create',
  async ({ campaignId, payload }, { dispatch, rejectWithValue }) => {
    try {
      const created = await createSubCampaignApi(campaignId, payload);
      dispatch(upsert({ campaignId, record: created }));
      await dispatch(loadSubCampaigns({ campaignId }));
      return { record: normalise(created), message: `“${created?.name || 'Sub-campaign'}” created` };
    } catch (error) {
      return rejectWithValue(error.message || 'Could not create the sub-campaign');
    }
  }
);

export const updateSubCampaign = createAsyncThunk(
  'emailSubCampaigns/update',
  async ({ campaignId, id, payload }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await updateSubCampaignApi(id, payload);
      dispatch(upsert({ campaignId, record: updated }));
      return { record: normalise(updated), message: 'Sub-campaign saved' };
    } catch (error) {
      return rejectWithValue(error.message || 'Could not save the sub-campaign');
    }
  }
);

export const removeSubCampaign = createAsyncThunk(
  'emailSubCampaigns/remove',
  async ({ campaignId, id }, { dispatch, rejectWithValue }) => {
    try {
      await deleteSubCampaignApi(id);
      dispatch(dropFromCache({ campaignId, id }));
      await dispatch(loadSubCampaigns({ campaignId }));
      return { message: 'Sub-campaign deleted' };
    } catch (error) {
      return rejectWithValue(error.message || 'Could not delete the sub-campaign');
    }
  }
);

export const duplicateSubCampaign = createAsyncThunk(
  'emailSubCampaigns/duplicate',
  async ({ campaignId, id, name }, { dispatch, rejectWithValue }) => {
    try {
      const copy = await duplicateSubCampaignApi(id, name);
      dispatch(upsert({ campaignId, record: copy }));
      await dispatch(loadSubCampaigns({ campaignId }));
      return { record: normalise(copy), message: 'Copied to a new draft' };
    } catch (error) {
      return rejectWithValue(error.message || 'Could not duplicate the sub-campaign');
    }
  }
);

/** Pause a running wave, or put a paused one back on its schedule. */
export const toggleSubCampaign = createAsyncThunk(
  'emailSubCampaigns/toggle',
  async ({ campaignId, id, action }, { dispatch, getState, rejectWithValue }) => {
    const current = selectSubCampaignById(getState(), campaignId, id);
    const next = action || (current?.status === 'paused' ? 'resume' : 'pause');

    // Optimistic: the table's status chip flips straight away.
    dispatch(
      upsert({
        campaignId,
        record: { ...current, _id: id, status: next === 'pause' ? 'paused' : 'scheduled' },
      })
    );

    try {
      const updated = await toggleSubCampaignApi(id, next);
      dispatch(upsert({ campaignId, record: updated }));
      return {
        record: normalise(updated),
        message: next === 'pause' ? 'Sub-campaign paused' : 'Sub-campaign resumed',
      };
    } catch (error) {
      if (current) dispatch(upsert({ campaignId, record: current }));
      return rejectWithValue(error.message || `Could not ${next} the sub-campaign`);
    }
  }
);

export const runSubCampaignNow = createAsyncThunk(
  'emailSubCampaigns/runNow',
  async ({ campaignId, id }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await runSubCampaignApi(id);
      dispatch(upsert({ campaignId, record: updated }));
      return { record: normalise(updated), message: 'Sending started' };
    } catch (error) {
      return rejectWithValue(error.message || 'Could not start sending');
    }
  }
);

export const saveSubCampaignSchedule = createAsyncThunk(
  'emailSubCampaigns/saveSchedule',
  async ({ campaignId, id, scheduling }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await updateSubCampaignScheduleApi(id, scheduling);
      dispatch(upsert({ campaignId, record: updated }));
      return { record: normalise(updated), message: 'Schedule updated' };
    } catch (error) {
      return rejectWithValue(error.message || 'Could not update the schedule');
    }
  }
);

export const assignSubCampaignTemplates = createAsyncThunk(
  'emailSubCampaigns/assignTemplates',
  async ({ campaignId, id, steps, mode = 'replace' }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await assignSubCampaignTemplatesApi(id, steps, mode);
      dispatch(upsert({ campaignId, record: updated }));
      return { record: normalise(updated), message: 'Templates assigned' };
    } catch (error) {
      return rejectWithValue(error.message || 'Could not assign templates');
    }
  }
);

export const assignSubCampaignRecipients = createAsyncThunk(
  'emailSubCampaigns/assignRecipients',
  async ({ campaignId, id, payload }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await assignSubCampaignRecipientsApi(id, payload);
      dispatch(upsert({ campaignId, record: updated }));
      return { record: normalise(updated), message: 'Recipients assigned' };
    } catch (error) {
      return rejectWithValue(error.message || 'Could not assign recipients');
    }
  }
);

export const bulkSubCampaign = createAsyncThunk(
  'emailSubCampaigns/bulk',
  async ({ campaignId, ids, action, status }, { dispatch, rejectWithValue }) => {
    try {
      const result = await bulkSubCampaignApi(campaignId, ids, action, status);
      await dispatch(loadSubCampaigns({ campaignId }));
      return {
        result,
        message: `${result.affected} sub-campaign${result.affected === 1 ? '' : 's'} updated`,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Bulk action failed');
    }
  }
);

export const loadSubCampaignOne = createAsyncThunk(
  'emailSubCampaigns/loadOne',
  async ({ campaignId, id }, { dispatch }) => {
    try {
      const record = await fetchSubCampaignById(id);
      if (record) dispatch(upsert({ campaignId, record }));
      return record ? normalise(record) : null;
    } catch (error) {
      console.error('Failed to load sub-campaign:', error);
      return null;
    }
  }
);

export const loadSubCampaignRecipients = createAsyncThunk(
  'emailSubCampaigns/loadRecipients',
  async ({ id, params }) => {
    try {
      return await fetchSubCampaignRecipients(id, params);
    } catch (error) {
      console.error('Failed to load sub-campaign recipients:', error);
      return { recipients: [], pagination: null };
    }
  }
);

// --- assignment map shared with the Recipients and Content tabs -------------

export const loadAssignments = createAsyncThunk(
  'emailSubCampaigns/loadAssignments',
  async (campaignId) => {
    if (!campaignId) return null;
    const key = String(campaignId);
    try {
      const data = await fetchSubCampaignAssignments(key);
      return { key, data };
    } catch (error) {
      console.error('Failed to load sub-campaign assignments:', error);
      return null;
    }
  }
);

/** mode: 'append' | 'remove' */
export const assignRecipientsToSubCampaigns = createAsyncThunk(
  'emailSubCampaigns/assignRecipientsToSubCampaigns',
  async ({ campaignId, subCampaignIds, emails, mode = 'append' }, { dispatch, rejectWithValue }) => {
    try {
      const result = await assignRecipientsToSubCampaignsApi(
        campaignId, subCampaignIds, emails, mode
      );
      // Both the map and the wave list moved, so refresh whichever is mounted.
      await Promise.all([
        dispatch(loadAssignments(campaignId)),
        dispatch(loadSubCampaigns({ campaignId })),
      ]);
      const plural = (n) => (n === 1 ? '' : 's');
      return {
        result,
        message:
          mode === 'remove'
            ? `Removed ${emails.length} recipient${plural(emails.length)} from ${subCampaignIds.length} sub-campaign${plural(subCampaignIds.length)}`
            : `Added ${emails.length} recipient${plural(emails.length)} to ${subCampaignIds.length} sub-campaign${plural(subCampaignIds.length)}`,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Could not update the assignment');
    }
  }
);

/** mode: 'append' | 'replace' | 'remove' */
export const assignTemplatesToSubCampaigns = createAsyncThunk(
  'emailSubCampaigns/assignTemplatesToSubCampaigns',
  async ({ campaignId, subCampaignIds, steps, mode = 'append' }, { dispatch, rejectWithValue }) => {
    try {
      const result = await assignTemplatesToSubCampaignsApi(
        campaignId, subCampaignIds, steps, mode
      );
      await Promise.all([
        dispatch(loadAssignments(campaignId)),
        dispatch(loadSubCampaigns({ campaignId })),
      ]);
      const plural = subCampaignIds.length === 1 ? '' : 's';
      return {
        result,
        message:
          mode === 'remove'
            ? `Template removed from ${subCampaignIds.length} sub-campaign${plural}`
            : `Template assigned to ${subCampaignIds.length} sub-campaign${plural}`,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Could not update the assignment');
    }
  }
);

export const previewSchedule = createAsyncThunk(
  'emailSubCampaigns/previewSchedule',
  async ({ campaignId, scheduling }) => {
    try {
      return await previewSubCampaignSchedule(campaignId, scheduling);
    } catch (error) {
      console.error('Failed to preview schedule:', error);
      return [];
    }
  }
);

const initialState = {
  // Keyed by parent campaign id so switching campaigns does not clobber the list.
  byCampaign: {},
  // The cross-cutting map the Recipients and Content tabs read, also keyed by campaign.
  assignmentsByCampaign: {},
  assignmentsLoading: false,
  query: { ...DEFAULT_QUERY },
  selected: [],
  loading: false,
  saving: false,
  error: null,
  lastMessage: null,
};

const emailSubCampaignsSlice = createSlice({
  name: 'emailSubCampaigns',
  initialState,
  reducers: {
    setQuery: (state, action) => {
      // Any filter change resets to page 1 unless the page itself is what changed.
      const patch = action.payload;
      const next = { ...state.query, ...patch };
      if (!('page' in patch)) next.page = 1;
      state.query = next;
    },
    setSelected: (state, action) => {
      state.selected = action.payload;
    },
    toggleSelected: (state, action) => {
      const id = action.payload;
      state.selected = state.selected.includes(id)
        ? state.selected.filter((value) => value !== id)
        : [...state.selected, id];
    },
    clearSelection: (state) => {
      state.selected = [];
    },
    clearMessage: (state) => {
      state.lastMessage = null;
      state.error = null;
    },
    // Merge one record back into the cached list without a full refetch.
    upsert: (state, action) => {
      const { campaignId, record } = action.payload;
      const key = String(campaignId);
      const bucket = state.byCampaign[key];
      if (!bucket) return;
      const row = normalise(record);
      const exists = bucket.items.some((item) => sameId(item, row.id));
      bucket.items = exists
        ? bucket.items.map((item) => (sameId(item, row.id) ? { ...item, ...row } : item))
        : [row, ...bucket.items];
    },
    dropFromCache: (state, action) => {
      const { campaignId, id } = action.payload;
      const key = String(campaignId);
      state.selected = state.selected.filter((value) => String(value) !== String(id));
      const bucket = state.byCampaign[key];
      if (bucket) bucket.items = bucket.items.filter((item) => !sameId(item, id));
    },
  },
  extraReducers: (builder) => {
    // Every write thunk shares the same saving/message/error bookkeeping.
    const savingPending = (state) => {
      state.saving = true;
      state.error = null;
    };
    const savingFulfilled = (state, action) => {
      state.saving = false;
      if (action.payload?.message) state.lastMessage = action.payload.message;
    };
    const savingRejected = (state, action) => {
      state.saving = false;
      state.error = action.payload;
    };

    builder
      .addCase(loadSubCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadSubCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload) return;
        const { key, items, summary, pagination } = action.payload;
        state.byCampaign[key] = { items, summary, pagination };
      })
      .addCase(loadSubCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(loadAssignments.pending, (state) => {
        state.assignmentsLoading = true;
      })
      .addCase(loadAssignments.fulfilled, (state, action) => {
        state.assignmentsLoading = false;
        if (action.payload) {
          state.assignmentsByCampaign[action.payload.key] = action.payload.data;
        }
      })
      .addCase(loadAssignments.rejected, (state) => {
        state.assignmentsLoading = false;
      })

      // toggle deliberately omits the saving flag, matching the old store.
      .addCase(toggleSubCampaign.fulfilled, savingFulfilled)
      .addCase(toggleSubCampaign.rejected, (state, action) => {
        state.error = action.payload;
      });

    for (const thunk of [
      createSubCampaign,
      updateSubCampaign,
      removeSubCampaign,
      duplicateSubCampaign,
      runSubCampaignNow,
      saveSubCampaignSchedule,
      assignSubCampaignTemplates,
      assignSubCampaignRecipients,
      assignRecipientsToSubCampaigns,
      assignTemplatesToSubCampaigns,
    ]) {
      builder
        .addCase(thunk.pending, savingPending)
        .addCase(thunk.fulfilled, savingFulfilled)
        .addCase(thunk.rejected, savingRejected);
    }

    builder
      .addCase(bulkSubCampaign.pending, savingPending)
      .addCase(bulkSubCampaign.fulfilled, (state, action) => {
        state.saving = false;
        state.selected = [];
        if (action.payload?.message) state.lastMessage = action.payload.message;
      })
      .addCase(bulkSubCampaign.rejected, savingRejected);
  },
});

export const {
  setQuery,
  setSelected,
  toggleSelected,
  clearSelection,
  clearMessage,
  upsert,
  dropFromCache,
} = emailSubCampaignsSlice.actions;

// Store getters become selectors.
export const selectSubCampaignList = (state, campaignId) =>
  state.emailSubCampaigns.byCampaign[String(campaignId)]?.items || [];

export const selectSubCampaignSummary = (state, campaignId) =>
  state.emailSubCampaigns.byCampaign[String(campaignId)]?.summary || EMPTY_SUMMARY;

export const selectSubCampaignPagination = (state, campaignId) =>
  state.emailSubCampaigns.byCampaign[String(campaignId)]?.pagination || null;

export const selectSubCampaignById = (state, campaignId, id) =>
  (state.emailSubCampaigns.byCampaign[String(campaignId)]?.items || []).find((row) =>
    sameId(row, id)
  ) || null;

export const selectAssignments = (state, campaignId) =>
  state.emailSubCampaigns.assignmentsByCampaign[String(campaignId)] || EMPTY_ASSIGNMENTS;

export default emailSubCampaignsSlice.reducer;
